import { styled } from '@mui/material';
import { ColDef, ICellRendererParams, GridOptions } from 'ag-grid-community';
import { AgGridReactProps } from 'ag-grid-react';
import { DEFAULT_PAGE_SIZE } from 'business/constants';
import { ChartSettingsT } from 'business/definitions';
import { getDecimalPrecision } from 'helpers/chart/settings';
import React, { useCallback, useEffect, useMemo } from 'react';
import { fractionAggFunc } from 'ui/AgGridReact/aggFuncs';
import {
    AGGRID_DEFAULT_COL_DEF,
    AGGRID_DEFAULT_ROW_HEIGHT,
} from 'ui/AgGridReact/constants';
import { useCanEdit } from '../hooks/canEdit';
import TableAgGrid from './TableAgGrid';
import {
    getEnrichedColumnWithColumnsHidden,
    getEnrichedColumnWithColumnsSorting,
    getEnrichedColumnWithColumnsWidth,
    getFormattedTableColumns,
    sortArrayFromColumnOrder,
} from './defaultSettings';
import { useGetDashboardFromSlugOrMetadata } from 'hooks/useGetDashboardFromSlugOrMetadata';

const ROW_HEIGHTS = {
    sm: 20,
    md: AGGRID_DEFAULT_ROW_HEIGHT,
    lg: 48,
};
const DEFAULT_ROW_HEIGHT = 'md';

const DEFAULT_SIDE_BAR = {
    toolPanels: [
        {
            id: 'columns',
            labelKey: 'columns',
            labelDefault: 'Columns',
            iconKey: 'columns',
            toolPanel: 'agColumnsToolPanel',
            toolPanelParams: {
                // Keep only columns visibility and order
                suppressPivotMode: true,
                suppressRowGroups: true,
                suppressValues: true,
            },
        },
    ],
};

const DEFAULT_GRID_PROPS: GridOptions = {
    alwaysMultiSort: true,
    suppressDragLeaveHidesColumns: true, // Remove drag to hide columns
};

const StyCell = styled('button', {
    name: 'StyCell',
})({
    width: '100%',
    height: '100%',
    backgroundColor: 'transparent',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
});

const getCellRenderer =
    (
        onClick: (
            point: ResponseDataT | undefined,
            selectedColumn?: string,
        ) => void,
    ) =>
    (params: ICellRendererParams) => {
        const onCellClick = () =>
            onClick(params.data, params.column?.getColId());

        return (
            <StyCell type="button" onClick={() => onCellClick()}>
                {params.valueFormatted}
            </StyCell>
        );
    };

type WrapperProps = {
    data: ResponseDataT[];
    orderedColumns: Expand<ResponseColumnT>[];
    settings: ChartSettingsT;
    className?: string;
    applyChartSettings: (arg1: ChartSettingsT) => void;
    autoSizeColumns?: boolean;
    autoSizeHeight?: boolean;
    hideSideBar?: boolean;
    isChartViewedOnDashboard?: boolean;
    disableSorting?: boolean;
    onClick?: (
        point: ResponseDataT | undefined,
        selectedColumn?: string,
    ) => void;
};

const TableAgChart = ({
    data,
    orderedColumns,
    settings,
    className,
    applyChartSettings,
    autoSizeColumns = false,
    autoSizeHeight = false,
    hideSideBar = false,
    disableSorting = false,
    isChartViewedOnDashboard = false,
    onClick = () => {},
}: WrapperProps): JSX.Element => {
    const dashboard = useGetDashboardFromSlugOrMetadata();

    const canEdit = useCanEdit(
        isChartViewedOnDashboard,
        dashboard?.canEdit || false,
    );

    const {
        pageSize,
        rowHeight: settingsRowHeight,
        displayBy,
        columnOrder,
        columnsHidden,
        columnsSorting,
        columnsWidth,
        hideToolbar,
        showFilters,
    } = settings;

    const dataLength = data.length;

    // This useEffect computes the default behaviour of the showFilters settings
    // most of our chart doesn't have this setting by default (legacy).
    // Thus if showFilters is not defined, we set it to according to its
    // former rule.
    useEffect(() => {
        if (showFilters === undefined) {
            applyChartSettings({
                ...settings,
                showFilters: !!pageSize && dataLength > pageSize,
            });
        }
    }, [applyChartSettings, dataLength, pageSize, showFilters]);

    const colDefs = useMemo(() => {
        const hideFilter = !showFilters;
        const formattedColumns = getFormattedTableColumns(
            orderedColumns,
            hideFilter,
        );
        const enrichedColumnWithHidden = getEnrichedColumnWithColumnsHidden(
            formattedColumns,
            columnsHidden,
        );
        const enrichedColumnWithSorting = getEnrichedColumnWithColumnsSorting(
            enrichedColumnWithHidden,
            columnsSorting,
        );
        const enrichedColumnWithWidth = getEnrichedColumnWithColumnsWidth(
            enrichedColumnWithSorting,
            columnsWidth,
        );
        if (!disableSorting) {
            return sortArrayFromColumnOrder(
                enrichedColumnWithWidth,
                columnOrder,
            );
        }

        return enrichedColumnWithWidth;
    }, [
        orderedColumns,
        columnsHidden,
        columnOrder,
        columnsSorting,
        columnsWidth,
        dataLength,
        pageSize,
        showFilters,
    ]);

    const rowHeight = useMemo(
        () => ROW_HEIGHTS[settingsRowHeight || DEFAULT_ROW_HEIGHT],
        [settingsRowHeight],
    );
    const isPaginationEnabled = data.length > (pageSize || DEFAULT_PAGE_SIZE);
    const decimalPrecision = getDecimalPrecision(settings);
    const gridProps: Partial<AgGridReactProps> = {
        context: {
            displayBy,
            decimalPrecision,
        },
        aggFuncs: { fractionAggFunc },
        pagination: isPaginationEnabled,
        paginationPageSize: pageSize,
        rowHeight,
        getRowHeight: (params) =>
            params.node.isRowPinned() ? ROW_HEIGHTS.lg : undefined,
        sideBar: !hideSideBar ? DEFAULT_SIDE_BAR : undefined,
        ...DEFAULT_GRID_PROPS,
    };

    if (hideToolbar) {
        gridProps.sideBar = undefined;
    }

    const updateSettings = useCallback(
        (newSettings: ChartSettingsT) => {
            if (!canEdit || !applyChartSettings) return;

            applyChartSettings({
                ...settings,
                ...newSettings,
            });
        },
        [applyChartSettings, canEdit, settings],
    );

    const defaultColDef: ColDef = {
        ...AGGRID_DEFAULT_COL_DEF,
        flex: columnsWidth === undefined ? 1 : undefined,
        lockVisible: !canEdit,
        cellRenderer: onClick && getCellRenderer(onClick),
        comparator: (valueA, valueB) => {
            if (valueA === valueB) return 0;
            const comp = valueA > valueB ? 1 : -1;
            return comp;
        },
    };

    return (
        <TableAgGrid
            data={data}
            colDefs={colDefs}
            className={className}
            gridProps={gridProps}
            chartSettings={settings}
            updateSettings={updateSettings}
            defaultColDef={defaultColDef}
            autoSizeColumns={autoSizeColumns}
            autoSizeHeight={autoSizeHeight}
        />
    );
};

export default TableAgChart;
